/**
 * AsaasOnboardingPage — cria a subconta Asaas e acompanha o envio dos
 * documentos, contra o SANDBOX.
 *
 * ⚠️ ESTA É A TELA DE CADASTRO/CRIAÇÃO DE SUBCONTA QUE O PLAYBOOK DE BaaS
 * EXIGE COMO EVIDÊNCIA (#219) — e por isso ela leva o selo do Asaas mesmo
 * sendo um caminho de teste: a exigência regulatória (Resolução Conjunta
 * nº 16/2025) não distingue ambiente.
 *
 * Por que esta tela existe com o BaaS ainda CONGELADO em produção (addendum
 * do #165): o congelamento é do lançamento para o USUÁRIO FINAL — a
 * detecção automática de pagamento continua invisível para todo mundo
 * (`AUTO_PAYMENT_DETECTION_FROZEN` em `paymentDetection.ts`). Esta tela é o
 * CAMINHO DE TESTE que o #166/#208 já documentam (`plan_type = 'admin'`),
 * agora com uma UI própria em vez de só scripts — decisão explícita de
 * sessão de 2026-08-10, para permitir tirar os prints que o playbook exige
 * como evidência, sem tocar no ambiente de produção.
 *
 * O portão é DUPLO e nenhuma metade substitui a outra:
 *  1. `useAsaasAccount` exige `isAdmin` (owner/admin) — mesmo papel que o
 *     servidor (`requireCompanyAdmin`) cobra;
 *  2. `asaas-onboard` exige plano `admin`/override (`resolveEntitlement`) —
 *     ninguém chega aqui assinando o PRO comum.
 * NÃO reusa `autoDetectionReachable()`: aquela função existe para calar o
 * bloco de PRODUÇÃO enquanto o BaaS não é aprovado — aplicá-la aqui
 * tornaria esta tela permanentemente inalcançável, o que destruiria o
 * próprio propósito dela (testar/gerar evidência em sandbox).
 *
 * O ambiente operado é o que a Edge Function decidir (`ASAAS_ENVIRONMENT`):
 * sandbox por padrão, e produção só se o segredo estiver explicitamente
 * configurado — decisão que não é deste código, é do deploy.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { FieldLabel, FIELD_INPUT, CONTENT_CARD } from '@/components/ui/field';
import { AsaasSeal } from '@/components/payments/AsaasSeal';
import {
  ASAAS_LEGAL_NAME,
  ASAAS_SHORT_DISCLOSURE,
  ASAAS_SUPPORT,
} from '@/lib/asaasDisclosure';
import { useAsaasAccount } from '@/hooks/useAsaasAccount';
import { maskBRLInput } from '@/lib/currencyInput';
import { formatCep, formatDocument, onlyDigits } from '@/lib/documentUtils';
import {
  ASAAS_DOCUMENT_STATUS_LABELS,
  EMPTY_ASAAS_ONBOARD_FORM,
  asaasDocumentIsCnpj,
  validateAsaasOnboardForm,
  type AsaasCompanyType,
  type AsaasOnboardFormValues,
} from '@/lib/asaasOnboarding';

const ACCOUNT_STATUS_LABELS: Record<string, string> = {
  pending: 'Em análise pelo Asaas',
  approved: 'Aprovada',
  disabled: 'Desabilitada',
};

/** Abre um link externo — web-only via `window.open` já é o padrão do app (#35h). */
function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * A atribuição COMPLETA ao Asaas: selo + menção textual + canais de suporte.
 *
 * É o mesmo trio do `PaymentDetectionCard`, e a razão de existir aqui é
 * constrangedora: até 2026-08-17 esta tela — a ÚNICA tela de produto que o
 * playbook nomeia como evidência ("Telas de produto → Criação de subconta") —
 * tinha MENOS atribuição do que um card que o playbook nem cita. Só o selo.
 *
 * Cada peça responde por uma coisa diferente, e nenhuma cobre a outra:
 *
 *  - o **selo** é o que o playbook exige na tela;
 *  - a **menção textual** é o que sobra quando a imagem não carrega — offline
 *    (a CDN não está no cache do app) ou por CSP, que já apagou o selo do app
 *    inteiro por quatro dias (#231). Ela nomeia a instituição responsável, que
 *    é o que a Resolução Conjunta nº 16/2025 cobra de fato;
 *  - os **canais** são exigência própria do playbook, e são os do ASAAS: quem
 *    abre uma conta de pagamento precisa saber que aciona o Asaas, não o
 *    Orçamigo — que não resolve (nem enxerga) o que é regulatório.
 *
 * Centralizada porque o selo é centralizado (#231) e o bloco lê como crédito de
 * rodapé; as duas linhas são curtas o bastante para isso não custar leitura.
 */
function AtribuicaoAsaas() {
  return (
    <div className="text-center">
      <AsaasSeal />
      <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
        {ASAAS_SHORT_DISCLOSURE}
      </p>
      <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
        Suporte do Asaas: {ASAAS_SUPPORT.phone} ({ASAAS_SUPPORT.phoneNote}) · {ASAAS_SUPPORT.email}
      </p>
    </div>
  );
}

export default function AsaasOnboardingPage() {
  const navigate = useNavigate();
  const {
    ready, account, accountLoading, documents, documentsRejectReasons,
    documentsLoading, documentsPending, documentsError, creating, createAccount,
  } = useAsaasAccount();

  const [values, setValues] = useState<AsaasOnboardFormValues>(EMPTY_ASAAS_ONBOARD_FORM);
  const [errors, setErrors] = useState<ReturnType<typeof validateAsaasOnboardForm>['errors']>({});

  const isCnpj = asaasDocumentIsCnpj(values.cpfCnpj);

  /**
   * O ramo do FORMULÁRIO — a mesma condição que o JSX abaixo usa para escolher
   * o `OnboardForm`. Extraída porque duas decisões dependem dela e precisam
   * concordar: qual bloco renderizar, e onde a atribuição ao Asaas mora. Se
   * divergirem, a tela mostra o selo duas vezes ou nenhuma.
   */
  const noFormulario = ready && !accountLoading && !account;

  const set = <K extends keyof AsaasOnboardFormValues>(key: K, value: AsaasOnboardFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    const validation = validateAsaasOnboardForm(values);
    setErrors(validation.errors);
    if (!validation.ok) return;
    try {
      await createAccount(values);
    } catch {
      // O toast já saiu no `onError` da mutation.
    }
  };

  /**
   * O valor ao vivo do hero — e a AUSÊNCIA dele é regra, não esquecimento
   * (#187/#199).
   *
   * No portão de papel e enquanto a consulta não volta, a tela NÃO SABE se
   * existe conta. Um hero anunciando "Nenhuma conta conectada" ali seria falso
   * de dois jeitos: para quem não é admin o problema não é a conta, é a
   * permissão; e "ainda não sei" não é "não existe" (#166 — estado desconhecido
   * nunca vira afirmação). Sem `resumo`, o bloco simplesmente não renderiza.
   */
  const resumo: { label: string; valor: ReactNode; subline: string } | null =
    !ready || accountLoading
      ? null
      : account
        ? {
            label: 'Situação da conta',
            valor: ACCOUNT_STATUS_LABELS[account.status] ?? account.status,
            subline:
              account.status === 'approved' && !account.pix_key_ready
                ? 'Aprovada, mas ainda sem chave Pix'
                : documentsPending
                  ? 'O Asaas ainda está validando o cadastro'
                  : 'Acompanhe os documentos abaixo',
          }
        : {
            label: 'Nova conta de pagamento',
            // O nome que está sendo DIGITADO, como nas outras telas de cadastro
            // (#160/#205). `line-clamp-2` e não `truncate`: é texto livre, e o
            // que o corte comeria é justamente o que identifica o cadastro.
            valor: values.name.trim() || <span className="text-white/60">Sem nome ainda</span>,
            subline: values.cpfCnpj
              ? formatDocument(values.cpfCnpj)
              : 'Preencha os dados para criar a subconta',
          };

  /**
   * O hero mora numa `const` porque a tela tem QUATRO estados (portão de papel,
   * carregando, formulário, conta criada) e o mesmo cabeçalho vale para os
   * quatro. Escrito inline em cada ramo, o gradiente estaria repetido quatro
   * vezes e a primeira mudança de desenho pegaria três — o motivo pelo qual o
   * `NfseHero` virou componente (#199).
   */
  const hero = (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={() => navigate(-1)}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">Pagamentos</p>
          {/* `line-clamp-2`: medido a 360px, este título não cabe numa linha ao
              lado do botão de voltar e da pílula de ambiente. */}
          <h1 className="line-clamp-2 text-lg font-bold leading-tight">
            Conectar conta de pagamento
          </h1>
        </div>
        {/* O AMBIENTE pela mesma régua da NFS-e (#204): conta criada em sandbox
            não existe para o mundo real, e isso muda o significado de tudo o que
            a tela mostra — então fica visível SEMPRE, no mesmo âmbar que a
            "Homologação" usa para dizer "isto não vale de verdade". */}
        <span className="shrink-0 rounded-full bg-amber-400 px-2.5 py-1 text-[11px] font-medium leading-none text-amber-950">
          Sandbox
        </span>
      </div>

      {resumo && (
        <div className="relative mt-5 min-w-0">
          <p className="text-xs text-white/75">{resumo.label}</p>
          <p className="line-clamp-2 break-words text-2xl font-bold leading-tight">
            {resumo.valor}
          </p>
          <p className="mt-0.5 text-xs text-white/70">{resumo.subline}</p>
        </div>
      )}
    </div>
  );

  return (
    <MainLayout showFab={false} width="form">
      <div className="pb-8">
        {hero}

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero */}
        <div className="relative z-10 -mt-9 space-y-4">
        {!ready ? (
          <div className={`${CONTENT_CARD} p-4 text-sm text-muted-foreground`}>
            Só o dono ou um administrador da empresa pode conectar a conta de pagamento.
          </div>
        ) : accountLoading ? (
          <div className={`${CONTENT_CARD} p-4 text-sm text-muted-foreground`}>Carregando…</div>
        ) : !account ? (
          <OnboardForm
            values={values}
            errors={errors}
            isCnpj={isCnpj}
            creating={creating}
            onChange={set}
            onSubmit={handleSubmit}
          />
        ) : (
          <div className="space-y-4">
            {/* A SITUAÇÃO agora é o valor ao vivo do hero; este cartão ficou só
                com o que ela não cabe dizer. Repetir "Situação da conta" aqui
                seria a duplicação que o card "Próximos passos" já custou uma
                vez (#121): dois blocos falando do mesmo estado, e o de baixo
                envelhecendo primeiro. Some inteiro quando não há aviso. */}
            {(account.status_detail ||
              (account.status === 'approved' && !account.pix_key_ready)) && (
              <div className={`${CONTENT_CARD} p-4`}>
                {account.status_detail && (
                  <p className="text-xs text-muted-foreground">{account.status_detail}</p>
                )}
                {account.status === 'approved' && !account.pix_key_ready && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    A conta foi aprovada, mas ainda não pode gerar QR Pix — confira se os
                    documentos abaixo foram todos aprovados (#170).
                  </p>
                )}
              </div>
            )}

            <div className={`${CONTENT_CARD} p-4`}>
              <p className="text-sm font-medium">Documentos</p>
              {documentsPending ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Aguarde alguns segundos — o Asaas ainda está validando o cadastro.
                </p>
              ) : documentsLoading ? (
                <p className="mt-2 text-xs text-muted-foreground">Carregando…</p>
              ) : documentsError ? (
                /*
                  A FALHA vem ANTES do vazio, e o ramo existe por isso: sem ele
                  uma consulta que não completou cai em "Nenhum documento
                  pendente" — o app afirmando que está tudo certo justamente
                  quando não conseguiu perguntar (#9). Aqui é `error` e não
                  `warning` porque não há nada que o usuário resolva digitando
                  (#26), e a mensagem do servidor é repassada porque ela é o
                  único fio que diz SE é cedo demais, sem rede ou função fora
                  do ar.
                */
                <p className="mt-2 text-xs text-destructive">
                  Não foi possível consultar os documentos: {documentsError}
                </p>
              ) : documents.length === 0 ? (
                <p className="mt-2 text-xs text-muted-foreground">Nenhum documento pendente.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {documents.map((group) => (
                    <li key={group.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{group.title}</p>
                          <p className="text-xs text-muted-foreground">{group.description}</p>
                        </div>
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                          {ASAAS_DOCUMENT_STATUS_LABELS[group.status]}
                        </span>
                      </div>
                      {group.onboardingUrl && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2"
                          onClick={() => openExternal(group.onboardingUrl!)}
                        >
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Enviar documento
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {documentsRejectReasons && (
                <p className="mt-2 text-xs text-destructive">{documentsRejectReasons}</p>
              )}
            </div>
          </div>
        )}

        {/*
          A atribuição aparece EXATAMENTE UMA VEZ em qualquer estado. No ramo do
          formulário ela mora no rodapé do próprio card (é onde o playbook a
          desenha, slide 10: dentro do modal, junto dos campos e do botão), e
          por isso o cartão avulso some ali. Nos demais estados não há
          formulário para hospedá-la, então ela vira cartão próprio.
        */}
        {!noFormulario && (
          <div className={`${CONTENT_CARD} p-4`}>
            <AtribuicaoAsaas />
          </div>
        )}
        </div>
      </div>
    </MainLayout>
  );
}

interface OnboardFormProps {
  values: AsaasOnboardFormValues;
  errors: ReturnType<typeof validateAsaasOnboardForm>['errors'];
  isCnpj: boolean;
  creating: boolean;
  onChange: <K extends keyof AsaasOnboardFormValues>(key: K, value: AsaasOnboardFormValues[K]) => void;
  onSubmit: () => void;
}

function OnboardForm({ values, errors, isCnpj, creating, onChange, onSubmit }: OnboardFormProps) {
  return (
    <div className={`${CONTENT_CARD} space-y-3 p-4`}>
      <div>
        <FieldLabel htmlFor="asaas-name">Nome do titular</FieldLabel>
        <input
          id="asaas-name"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.name}
          onChange={(e) => onChange('name', e.target.value)}
        />
        {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
      </div>

      <div>
        <FieldLabel htmlFor="asaas-email">E-mail</FieldLabel>
        <input
          id="asaas-email"
          type="email"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.email}
          onChange={(e) => onChange('email', e.target.value)}
        />
        {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
      </div>

      <div>
        <FieldLabel htmlFor="asaas-doc">CPF ou CNPJ do titular</FieldLabel>
        <input
          id="asaas-doc"
          inputMode="numeric"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={formatDocument(values.cpfCnpj)}
          onChange={(e) => onChange('cpfCnpj', onlyDigits(e.target.value))}
        />
        {errors.cpfCnpj && <p className="mt-1 text-xs text-destructive">{errors.cpfCnpj}</p>}
      </div>

      {isCnpj ? (
        <div>
          <FieldLabel htmlFor="asaas-company-type">Tipo de empresa</FieldLabel>
          <select
            id="asaas-company-type"
            className={`w-full px-3 ${FIELD_INPUT}`}
            value={values.companyType}
            onChange={(e) => onChange('companyType', e.target.value as AsaasCompanyType)}
          >
            <option value="MEI">MEI</option>
            <option value="LIMITED">Limitada</option>
            <option value="INDIVIDUAL">Empresário individual</option>
            <option value="ASSOCIATION">Associação</option>
          </select>
        </div>
      ) : (
        <div>
          <FieldLabel htmlFor="asaas-birth">Data de nascimento do titular</FieldLabel>
          <input
            id="asaas-birth"
            type="date"
            className={`w-full px-3 ${FIELD_INPUT}`}
            value={values.birthDate}
            onChange={(e) => onChange('birthDate', e.target.value)}
          />
          {errors.birthDate && <p className="mt-1 text-xs text-destructive">{errors.birthDate}</p>}
        </div>
      )}

      <div>
        <FieldLabel htmlFor="asaas-phone">Celular (com DDD)</FieldLabel>
        <input
          id="asaas-phone"
          inputMode="numeric"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.mobilePhone}
          onChange={(e) => onChange('mobilePhone', e.target.value)}
        />
        {errors.mobilePhone && <p className="mt-1 text-xs text-destructive">{errors.mobilePhone}</p>}
      </div>

      {/*
        Faturamento mensal — obrigatório na API do Asaas (`POST /v3/accounts`),
        e o único campo da tela de referência do playbook sem a marca
        "(Opcional)". Entra por MÁSCARA, nunca por digitação livre: em pt-BR o
        ponto é separador de milhar e no teclado do celular ele sai como
        decimal, então "30.50" é ambíguo entre R$ 3.050 e R$ 30,50 e nenhum
        parser adivinha (#157/#209). A máscara mata a ambiguidade na origem —
        os dígitos entram pela direita, em centavos.
      */}
      <div>
        <FieldLabel htmlFor="asaas-income">Faturamento ou renda mensal (R$)</FieldLabel>
        <input
          id="asaas-income"
          inputMode="numeric"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.incomeValue}
          onChange={(e) => onChange('incomeValue', maskBRLInput(e.target.value))}
        />
        {errors.incomeValue && <p className="mt-1 text-xs text-destructive">{errors.incomeValue}</p>}
      </div>

      <div>
        <FieldLabel htmlFor="asaas-cep">CEP</FieldLabel>
        <input
          id="asaas-cep"
          inputMode="numeric"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={formatCep(values.postalCode)}
          onChange={(e) => onChange('postalCode', onlyDigits(e.target.value))}
        />
        {errors.postalCode && <p className="mt-1 text-xs text-destructive">{errors.postalCode}</p>}
      </div>

      <div className="grid grid-cols-[1fr_auto] gap-2">
        <div>
          <FieldLabel htmlFor="asaas-address">Logradouro</FieldLabel>
          <input
            id="asaas-address"
            className={`w-full px-3 ${FIELD_INPUT}`}
            value={values.address}
            onChange={(e) => onChange('address', e.target.value)}
          />
          {errors.address && <p className="mt-1 text-xs text-destructive">{errors.address}</p>}
        </div>
        <div>
          <FieldLabel htmlFor="asaas-number">Número</FieldLabel>
          <input
            id="asaas-number"
            className={`w-20 px-3 ${FIELD_INPUT}`}
            value={values.addressNumber}
            onChange={(e) => onChange('addressNumber', e.target.value)}
          />
          {errors.addressNumber && <p className="mt-1 text-xs text-destructive">{errors.addressNumber}</p>}
        </div>
      </div>

      <div>
        <FieldLabel htmlFor="asaas-complement">Complemento (opcional)</FieldLabel>
        <input
          id="asaas-complement"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.complement}
          onChange={(e) => onChange('complement', e.target.value)}
        />
      </div>

      <div>
        <FieldLabel htmlFor="asaas-province">Bairro</FieldLabel>
        <input
          id="asaas-province"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.province}
          onChange={(e) => onChange('province', e.target.value)}
        />
        {errors.province && <p className="mt-1 text-xs text-destructive">{errors.province}</p>}
      </div>

      <Button3D disabled={creating} onClick={onSubmit} className="w-full">
        {creating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Criando…
          </>
        ) : (
          'Criar subconta'
        )}
      </Button3D>

      {/*
        A atribuição fecha o próprio card do formulário, como no slide 10 do
        playbook — o selo dentro do modal, junto dos campos e do botão. Fora
        dele (num cartão separado abaixo) um revisor pode ler o bloco como
        desligado do cadastro, e é o cadastro que ele veio conferir.
      */}
      <div className="mt-4 border-t border-border pt-4">
        <AtribuicaoAsaas />
      </div>
    </div>
  );
}
